import { useState, type FormEvent } from "react";
import { useCMSPage } from "../../../context/CMSContext";
import { useCMSGlobal } from "../../../hooks/useCMSField";
import { formsApi } from "../../../lib/api";

export default function ContactHero() {
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount]   = useState(0);

  const { data: contactCMS } = useCMSPage("contact");
  const globalCMS = useCMSGlobal();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const heroCMS: any = (contactCMS as any)?.hero   ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formCMS: any = (contactCMS as any)?.form   ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const infoCMS: any = (contactCMS as any)?.info   ?? {};

  const headline       = heroCMS?.headline     ?? "Contact us";
  const subheadline    = heroCMS?.subheadline  ?? "Whether you need help with a quote, have a general question, or just want to say hello — we're here 7 days a week.";
  const formHeadline   = formCMS?.headline     ?? "Send us a message.";
  const successMsg     = formCMS?.success_message ?? "Thanks! We'll get back to you within 2 hours.";
  const ctaLabel       = formCMS?.cta_label    ?? "Send message";

  const phone  = infoCMS?.phone  ?? globalCMS?.phone ?? "03335 775445";
  const email  = infoCMS?.email  ?? globalCMS?.email ?? "hello@cleario.co.uk";
  const hours  = infoCMS?.hours  ?? "Mon–Sat 7am–8pm, Sun 8am–6pm";

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setSubmitting(true);
    try {
      await formsApi.contact({
        first_name: (form.elements.namedItem("first_name") as HTMLInputElement)?.value ?? "",
        last_name:  (form.elements.namedItem("last_name")  as HTMLInputElement)?.value ?? "",
        email:      (form.elements.namedItem("email")      as HTMLInputElement)?.value ?? "",
        phone:      (form.elements.namedItem("phone")      as HTMLInputElement)?.value ?? "",
        message,
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pt-32 pb-20 bg-cl-tint">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left — intro */}
          <div className="pt-4">
            <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-5 thick-border cartoon-shadow-sm">
              <i className="ri-customer-service-2-line text-cl-dark text-xs"></i>
              <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Need some help</span>
            </div>

            <h1 className="font-black text-cl-dark leading-[1.05] mb-5" style={{ fontSize: "clamp(2.6rem, 5vw, 4rem)" }}>
              {headline}
            </h1>

            <p className="text-cl-dark/65 text-base leading-relaxed font-semibold mb-8 max-w-md">
              {subheadline}
            </p>

            {/* Contact info cards */}
            <div className="flex flex-col gap-3 mb-8">
              {[
                { icon: "ri-phone-fill",   label: "Call us",       value: phone, href: `tel:${phone.replace(/\s/g, "")}` },
                { icon: "ri-mail-fill",    label: "Email us",      value: email, href: `mailto:${email}` },
                { icon: "ri-time-line",    label: "Opening hours", value: hours, href: null },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4 bg-white rounded-2xl px-5 py-4 cartoon-card">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-dark flex-shrink-0">
                    <i className={`${item.icon} text-cl-dark text-base`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-0.5">{item.label}</p>
                    {item.href
                      ? <a href={item.href} className="font-black text-cl-dark text-sm hover:text-cl-green transition-colors">{item.value}</a>
                      : <p className="font-semibold text-cl-dark text-sm">{item.value}</p>
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — contact form */}
          <div className="bg-white rounded-3xl cartoon-card p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                <div className="w-16 h-16 bg-cl-green rounded-full flex items-center justify-center border-4 border-[#4a8c0a] cartoon-shadow-green">
                  <i className="ri-check-line text-white text-3xl"></i>
                </div>
                <h3 className="font-black text-cl-dark text-xl">{successMsg}</h3>
              </div>
            ) : (
              <>
                <h2 className="font-black text-cl-dark text-2xl mb-6">{formHeadline}</h2>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">First Name</label>
                      <input name="first_name" type="text" required placeholder="Jane"
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none focus:border-cl-green" />
                    </div>
                    <div>
                      <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">Last Name</label>
                      <input name="last_name" type="text" required placeholder="Smith"
                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none focus:border-cl-green" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">Email</label>
                    <input name="email" type="email" required placeholder="tom@example.co.uk"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none focus:border-cl-green" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">Phone (optional)</label>
                    <input name="phone" type="tel" placeholder="07700 900000"
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none focus:border-cl-green" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-1.5">
                      Message <span className="text-gray-300 font-normal normal-case">({charCount}/500)</span>
                    </label>
                    <textarea name="message" required rows={5} placeholder="Tell us what you need..."
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-semibold focus:outline-none focus:border-cl-green resize-none" />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-cl-green text-white font-black py-4 rounded-xl text-sm cartoon-btn-green disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitting ? <><span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin"></span> Sending…</> : <><i className="ri-send-plane-fill"></i> {ctaLabel}</>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
